import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/base/ArticleCard";
import { articles } from "@/mocks/articles";
import { topics, topicNames } from "@/mocks/topics";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";

export default function Ranking() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") ?? "全部";
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    return articles.filter((item) => {
      const matchCategory = category === "全部" || item.category === category;
      const matchText =
        !text ||
        item.title.toLowerCase().includes(text) ||
        item.excerpt.toLowerCase().includes(text) ||
        item.tags.some((tag) => tag.toLowerCase().includes(text));
      return matchCategory && matchText;
    });
  }, [category, query]);

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === "全部") params.delete("category");
      else params.set("category", next.category);
    }
    if (next.q !== undefined) {
      if (!next.q) params.delete("q");
      else params.set("q", next.q);
    }
    setSearchParams(params);
  };

  const activeTopic = topics.find((topic) => topic.name === category);

  useSeo({
    title:
      category === "全部"
        ? "打榜观察｜票数异常、粉丝内战与赛制争议全记录 - 瓜圈显微镜"
        : `${category}｜选美吃瓜投票打榜内容合集 - 瓜圈显微镜`,
    description:
      category === "全部"
        ? "打榜观察栏目汇总选美吃瓜投票打榜的完整记录：票数异常时段复盘、粉丝内战拉票实录、打榜成本调查与赛制争议分析，按栏目筛选即可深入阅读。"
        : `打榜观察栏目下的${category}合集，围绕选美吃瓜投票打榜整理相关内容，涵盖票数异常、拉票操作与规则解读，帮助你理解榜单背后的真实生态。`,
    keywords: `选美吃瓜,选美吃瓜投票,${category},选美吃瓜打榜,选美吃瓜票数`,
    path: category === "全部" ? "/ranking" : `/ranking?category=${encodeURIComponent(category)}`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: category === "全部" ? "打榜观察" : `${category}合集`,
        url: `${SITE_ORIGIN}/ranking`,
        inLanguage: "zh-CN",
        description: "选美吃瓜投票打榜环节的内容合集与数据观察。",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: filtered.slice(0, 10).map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `${SITE_ORIGIN}/article/${item.id}`,
            name: item.title,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "打榜观察", item: `${SITE_ORIGIN}/ranking` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="bg-background-100 border-b border-background-200">
          <div className="px-4 md:px-6 xl:px-10 py-8 md:py-12">
            <div className="max-w-[1320px] mx-auto">
              <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "打榜观察" }]} />
              <div className="mt-6 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
                <div className="max-w-[720px]">
                  <p className="text-xs tracking-[0.3em] text-primary-600 mb-2">RANKING WATCH</p>
                  <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">
                    {category === "全部" ? "打榜观察" : category}
                  </h1>
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                    {activeTopic
                      ? activeTopic.desc
                      : "把所有和投票打榜有关的观察放在一起：异常票数、拉票操作、成本调查与规则争议，你可以按栏目筛选，也可以直接搜关键词。"}
                  </p>
                </div>

                <form
                  role="search"
                  className="w-full lg:w-[340px] shrink-0"
                  onSubmit={(event) => {
                    event.preventDefault();
                    updateParams({ q: keyword.trim() });
                  }}
                >
                  <label htmlFor="ranking-search" className="sr-only">
                    搜索内容
                  </label>
                  <div className="flex items-center gap-2 bg-background-50 border border-background-300 rounded-md px-3 focus-within:border-primary-400">
                    <i className="ri-search-line text-foreground-500 w-5 h-5 flex items-center justify-center" />
                    <input
                      id="ranking-search"
                      type="search"
                      name="q"
                      value={keyword}
                      onChange={(event) => setKeyword(event.target.value)}
                      placeholder="搜索标题或标签"
                      className="w-full bg-transparent py-3 text-sm outline-none placeholder:text-foreground-400"
                    />
                    <button
                      type="submit"
                      className="text-sm font-medium text-primary-700 whitespace-nowrap cursor-pointer px-1"
                    >
                      搜索
                    </button>
                  </div>
                </form>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                {topicNames.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => updateParams({ category: name })}
                    className={[
                      "px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer border",
                      name === category
                        ? "bg-primary-500 border-primary-500 text-background-50"
                        : "bg-background-50 border-background-300 text-foreground-700 hover:border-primary-300",
                    ].join(" ")}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 xl:px-10 py-10 md:py-14">
          <div className="max-w-[1320px] mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <p className="text-sm text-foreground-600">
                共 <strong className="text-foreground-950 font-semibold">{filtered.length}</strong> 篇内容
                {query && (
                  <span>
                    ，关键词「<span className="text-primary-700">{query}</span>」
                  </span>
                )}
              </p>
              {(query || category !== "全部") && (
                <button
                  type="button"
                  onClick={() => updateParams({ category: "全部", q: "" })}
                  className="text-sm text-foreground-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-circle-line" />
                  清除筛选
                </button>
              )}
            </div>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 border border-dashed border-background-300 rounded-lg">
                <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500 mb-4">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <h2 className="font-heading text-lg font-bold text-foreground-950">没有找到相关内容</h2>
                <p className="mt-2 text-sm text-foreground-600">
                  换个关键词试试，或者直接浏览全部栏目。
                </p>
                <button
                  type="button"
                  onClick={() => updateParams({ category: "全部", q: "" })}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  查看全部内容
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="bg-background-100 border-t border-background-200">
          <div className="px-4 md:px-6 xl:px-10 py-12 md:py-16">
            <div className="max-w-[1320px] mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                按栏目继续看
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {topics
                  .filter((topic) => topic.name !== category)
                  .map((topic) => (
                    <Link
                      key={topic.slug}
                      to={`/ranking?category=${encodeURIComponent(topic.name)}`}
                      className="flex items-center gap-4 p-4 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 shrink-0">
                        <i className={`${topic.icon} text-lg`} />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-heading text-base font-bold text-foreground-950">
                          {topic.name}
                        </span>
                        <span className="block text-xs text-foreground-500 mt-0.5">{topic.count} 篇内容</span>
                      </span>
                      <i className="ri-arrow-right-line ml-auto text-foreground-400" />
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}